import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { SelectGroupOption } from './select-group';
import Icon, { ICONS, COLORS } from './icon';

export const DROP_DOWN_POSITIONS = {
	LEFT: 'drop-left',
	RIGHT: 'drop-right',
	DOWN_LEFT: 'drop-down-left',
	DOWN_RIGHT: 'drop-down-right',
};

export interface SelectGroupProps {
	options: SelectGroupOption[],
	onChange: (event: any) => any;
	label: string;
	selected: SelectGroupOption;
	invalid?: boolean;
	disabled?: boolean;
	dropdown?: boolean;
	position?: string;
	dropdownPosition?: string;
	optionHeight?: number;
	showLabel?: boolean;
	openAtSelected?: boolean;
	className?: string;
	// force menu to open upward
	forceOpenUp?: boolean;
	isOpen?: boolean;
	onBlur?: Function;
	onFocus?: Function;
	hasCarrot?: boolean;
}

const getShiftedCSS = (
	openAtSelected: boolean,
	selectedIndex: number,
	optionHeight: number,
	forceOpenUp: boolean,
	options: SelectGroupOption[],
) => {
	let top = 0;
	if (forceOpenUp) {
		top = options.length >= 6
			? optionHeight * -6
			: (options.length - 1) * optionHeight * -1;
	}

	return { top: openAtSelected ? `${top}px` : '' };
};

export default function SelectSmall(props: SelectGroupProps) {
	const {
		options,
		onChange,
		label,
		selected,
		invalid,
		disabled,
		dropdown,
		position,
		dropdownPosition,
		optionHeight = 36,
		showLabel = false,
		openAtSelected = true,
		className,
		forceOpenUp = false,
		isOpen = false,
		onBlur = () => {},
		onFocus = () => { },
		hasCarrot = false,
	} = props;

	const [open, setOpen] = useState(false);
	const [activeClass, setActiveClass] = useState('closed');
	const [selectedIndex, setSelectedIndex] = useState(
		options.findIndex((o) => o.value === selected.value),
	);
	const [shiftedBy, setShiftedBy] = useState(
		getShiftedCSS(openAtSelected, selectedIndex, optionHeight, forceOpenUp, options),
	);

	const minWidth = useMemo(() => {
		let longest = 0;
		options.forEach((op: SelectGroupOption) => {
			// find longest word
			longest = op.label.length > longest ? op.label.length : longest;
		});

		let calcedWidth = longest * 18; // 18px per letter?
		if (calcedWidth > 190) {
			calcedWidth = 190; // max min width
		} else if (calcedWidth < 100) {
			calcedWidth = 100; // min min width
		}
		return calcedWidth;
	}, [options]);

	function animationFinished() {
		if (open && activeClass === 'closed') {
			setOpen(false);
			setShiftedBy(getShiftedCSS(
				openAtSelected,
				selectedIndex,
				optionHeight,
				forceOpenUp,
				options,
			));
		}
	}

	function close() {
		onBlur();
		setActiveClass('closed');
	}

	function doOpen() {
		onFocus();
		setShiftedBy(getShiftedCSS(
			openAtSelected,
			selectedIndex,
			optionHeight,
			forceOpenUp,
			options,
		));
		setOpen(true);
		setTimeout(() => {
			setActiveClass('open');
		}, 16);

		if (openAtSelected) {
			setTimeout(() => {
				const element = document.getElementById('select-scroll-to');
				if (element) {
					element.scrollTo(0, selectedIndex * optionHeight);
				}
			}, 50);
		}
	}

	useEffect(() => {
		if (isOpen) {
			doOpen();
		} else {
			close();
		}
	}, [isOpen]);

	return (
		<div className={classNames('select-small', className || '', {
			open,
			invalid: invalid || false,
			disabled,
			title: showLabel,
			'virtuoso-scroller': open,
		}, position)}>
			{showLabel && <label className="select-title" onClick={doOpen}>{label}</label>}
			<span
				className={classNames(
					'select-label',
					{
						dropdown,
						'has-icon': selected?.icon,
					},
				)}
				onClick={doOpen}
			>
				<span className={classNames(
					'select-label-text',
					'truncate-overflow',
					'overflow-visible',
					selected.className,
				)}>
					{selected?.icon && (
						<span className="small-select-icon">
							<Icon name={selected.icon} color={COLORS.WHITE} size={selected?.iconSize ?? 13} />
						</span>
					)}
					{selected?.shortLabel ?? selected.label}
				</span>
				{dropdown && (
					<Icon
						name={ICONS.KEYBOARD_ARROW_DOWN}
						color={COLORS.DEFAULT_GRAY}
						size={10}
					/>
				)}
			</span>
			<div className="select-background" onClick={close}></div>
			{open && <div
				onClick={doOpen}
				className={classNames(
					'select-body',
					activeClass,
					dropdownPosition,
					{ 'scroller-border-right-radius': (options?.length ?? 0) * 46 >= 300 },
				)}
				style={{ ...shiftedBy, minWidth }}
				onTransitionEnd={animationFinished}
				id="select-scroll-to"
			>
				{options.map((opt, index) => (
					<React.Fragment key={index}>
						{opt?.icon && (
							<div
								className={classNames('select-small-row', {
									selected: (opt.value === selected.value && activeClass !== 'closed'),
									'selected-no-right-border-radius': (options?.length ?? 0) * 46 >= 300,
								})}
								onClick={(e) => {
									e.stopPropagation();
									onChange(opt.value);
									setSelectedIndex(index);
									close();
								}}
							>
								{opt?.icon && (
									<Icon name={opt.icon} size={opt?.iconSize ?? 12} color={COLORS.WHITE} />
								)}
								<div
									key={opt.value}
									className="select-option"
									style={{ height: optionHeight }}
								>
									<Icon size={15} color={COLORS.WHITE} name={ICONS.CHECK} />
									{opt?.description ? (
										<div className="option-with-description">
											<label className={classNames(opt.className)}>{opt.label}</label>
											<span>{opt.description}</span>
										</div>
									) : <label className={classNames(opt.className)}>{opt.label}</label>}
								</div>
							</div>
						)}
						{!opt?.icon && (
							<div
								key={opt.value}
								onClick={(e) => {
									e.stopPropagation();
									onChange(opt.value);
									setSelectedIndex(index);
									close();
								}}
								className={classNames('select-option', {
									selected: (opt.value === selected.value && activeClass !== 'closed'),
									'selected-no-right-border-radius': (options?.length ?? 0) * 46 >= 300,
								})}
								style={{ height: optionHeight }}
							>
								<Icon size={15} color={COLORS.WHITE} name={ICONS.CHECK} />
								{opt?.description ? (
									<div className="option-with-description">
										<label className={classNames(opt.className)}>{opt.label}</label>
										<span>{opt.description}</span>
									</div>
								) : <label className={classNames(opt.className)}>{opt.label}</label>}
							</div>
						)}
					</React.Fragment>
				))}
			</div>}
			{hasCarrot && (
				<div className="select-small-carrot">
					<Icon
						name={ICONS.KEYBOARD_ARROW_DOWN}
						color={COLORS.LIGHT_GRAY}
						size={12}
					/>
				</div>
			)}
		</div >
	);
}
